import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { cx } from '@/styled-system/css';
import {
  tooltipArrowBorderStyles,
  tooltipArrowStyles,
  tooltipContentStyles,
  tooltipPortalStyles,
  tooltipTriggerStyles,
} from './styles';
import { useDesktopTooltip, useDeviceDetection, useMobileTooltip } from './hooks';
import type { TooltipSize, TooltipV2Props } from './types';

const ARROW_SIZE = 6;

// Size mapping for responsive tooltip widths
const getSizeWidths = (size: TooltipSize): { desktop: string; mobile: string } => {
  switch (size) {
    case 'sm':
      return { desktop: '280px', mobile: '280px' };
    case 'md':
      return { desktop: '358px', mobile: '320px' };
    case 'lg':
      return { desktop: '450px', mobile: '320px' };
    case 'xl':
      return { desktop: '600px', mobile: '320px' };
    case 'auto':
      return { desktop: 'fit-content', mobile: 'fit-content' };
    default:
      return { desktop: '358px', mobile: '320px' };
  }
};

const TooltipV2: React.FC<TooltipV2Props> = ({
  children,
  content,
  position = 'above',
  disabled = false,
  className,
  size = 'md',
  delay = 0,
  offset = 4,
  positionStrategy = 'viewport-center',
  'data-testid': testId,
}) => {
  const { isMobile, isTouch } = useDeviceDetection();
  const portalContainer = useRef<HTMLDivElement>();
  const [isVisible, setIsVisible] = useState(false);

  // Calculate responsive widths based on size prop
  const sizeWidths = getSizeWidths(size);
  const currentWidth = isMobile ? sizeWidths.mobile : sizeWidths.desktop;
  const isAutoSize = size === 'auto';

  // Use appropriate hook based on device type
  const desktopHook = useDesktopTooltip(position, offset, size);
  const mobileHook = useMobileTooltip(position, offset, positionStrategy, size);

  // Select the appropriate hook based on device detection
  const {
    isOpen,
    position: calculatedPosition,
    coordinates,
    arrowOffset,
    triggerRef,
    tooltipRef,
  } = isMobile ? mobileHook : desktopHook;

  // Get device-specific handlers
  const { handleMouseEnter, handleMouseLeave, handleKeyDown: desktopHandleKeyDown } = desktopHook;
  const { handleMobileClick, handleKeyDown: mobileHandleKeyDown } = mobileHook;

  // Select appropriate keyboard handler
  const handleKeyDown = isMobile ? mobileHandleKeyDown : desktopHandleKeyDown;

  // Create portal container
  useEffect(() => {
    if (!portalContainer.current) {
      portalContainer.current = document.createElement('div');
      portalContainer.current.className = tooltipPortalStyles;
      portalContainer.current.id = 'tooltip-portal-container';
      document.body.appendChild(portalContainer.current);
    }

    return () => {
      if (portalContainer.current && document.body.contains(portalContainer.current)) {
        document.body.removeChild(portalContainer.current);
      }
    };
  }, []);

  // Handle fade transitions - separate DOM insertion from visibility
  useEffect(() => {
    if (isOpen && coordinates && portalContainer.current) {
      // Start fade-in animation on next frame after DOM insertion
      requestAnimationFrame(() => {
        setIsVisible(true);
      });
    } else {
      // Immediately hide for fade-out
      setIsVisible(false);
    }
  }, [isOpen, coordinates]);

  // Generate unique ID for accessibility
  const tooltipId = React.useId();

  const shouldRenderTooltip = isOpen && coordinates && portalContainer.current;

  const tooltipContent = shouldRenderTooltip
    ? createPortal(
        <div
          ref={tooltipRef}
          id={tooltipId}
          role="tooltip"
          data-visible={isVisible}
          className={cx(tooltipContentStyles({ position: calculatedPosition }), className)}
          style={{
            top: coordinates.top,
            left: coordinates.left,
            width: currentWidth,
            ...(isAutoSize && {
              padding: '8px',
              whiteSpace: 'nowrap',
            }),
          }}
          data-testid={testId ? `${testId}-content` : 'tooltip-content'}
        >
          {content}
          {/* Arrow border (larger, border color) */}
          <div
            className={tooltipArrowBorderStyles({ position: calculatedPosition })}
            style={
              isMobile
                ? {
                    left: `calc(50% + ${arrowOffset}px)`,
                    marginLeft: -7, // 7px for border arrow
                  }
                : {
                    left: `calc(50% + ${arrowOffset}px)`,
                    marginLeft: -7, // 7px for border arrow
                  }
            }
          />
          {/* Arrow fill (smaller, background color) */}
          <div
            className={tooltipArrowStyles({ position: calculatedPosition })}
            style={
              isMobile
                ? {
                    left: `calc(50% + ${arrowOffset}px)`,
                    marginLeft: -ARROW_SIZE,
                  }
                : {
                    left: `calc(50% + ${arrowOffset}px)`,
                    marginLeft: -ARROW_SIZE,
                  }
            }
          />
        </div>,
        portalContainer.current!,
      )
    : null;

  // Early return if disabled
  if (disabled) {
    return <>{children}</>;
  }

  return (
    <>
      <div
        ref={triggerRef}
        className={cx(tooltipTriggerStyles)}
        // Desktop events
        onMouseEnter={!isMobile ? handleMouseEnter : undefined}
        onMouseLeave={!isMobile ? handleMouseLeave : undefined}
        // Mobile events - unified click handling for all mobile browsers
        onClick={isMobile ? handleMobileClick : undefined}
        // Keyboard events (both desktop and mobile)
        onKeyDown={handleKeyDown}
        tabIndex={0}
        aria-describedby={isOpen ? tooltipId : undefined}
        data-testid={testId}
        style={{
          // Disable iOS tap highlight for mobile
          WebkitTapHighlightColor: isMobile ? 'transparent' : undefined,
          // Industry standard: disable double-tap zoom but allow scrolling
          touchAction: isMobile ? 'manipulation' : undefined,
        }}
      >
        {children}
      </div>
      {tooltipContent}
    </>
  );
};

export default TooltipV2;
